import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchHistory,
  fetchHistoryFilters,
  NO_DEPARTMENT,
  UNASSIGNED,
  type InboxPriorityValue,
  type InboxStatusValue,
} from '../lib/history-api';
import { formatDateTime } from '../lib/datetime';
import { visitorDisplayName } from '../lib/visitor-name';
import { useSiteNames, siteBadgeStyle } from '../stores/sites';
import { useMonitorUi } from '../stores/monitor-ui';
import styles from './ChatHistory.module.css';

// Server-driven chat history — the agent inbox: filter by status / priority /
// tag / assignee / site / date / location / engaged + full-text search, all
// resolved in the database with pagination.
// Filters + the last-fetched page live in the monitor-ui store (not local
// state) so leaving for a conversation and coming back restores everything and
// refreshes in the background instead of reloading blank.
// The server caps a page at 100 rows (/api/conversations/search); offering
// more made the pager count pages the server never returns.
const PAGE_SIZES = [25, 50, 100] as const;

const STATUS_LABEL: Record<InboxStatusValue, string> = {
  open: 'Open',
  pending: 'Pending',
  resolved: 'Resolved',
  closed: 'Closed',
};
const PRIORITY_LABEL: Record<InboxPriorityValue, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

export function ChatHistory() {
  const navigate = useNavigate();
  const { byId: siteById } = useSiteNames();

  const query = useMonitorUi((s) => s.historyQuery);
  const patch = useMonitorUi((s) => s.patchHistoryQuery);
  const resetQuery = useMonitorUi((s) => s.resetHistoryQuery);
  const result = useMonitorUi((s) => s.historyResult);
  const setResult = useMonitorUi((s) => s.setHistoryResult);
  const filters = useMonitorUi((s) => s.historyFilters);
  const setFilters = useMonitorUi((s) => s.setHistoryFilters);

  const items = result?.items ?? [];
  const total = result?.total ?? 0;
  // Full-screen "Loading…" only when there's nothing cached yet; later fetches
  // keep the cached rows on screen (see the render guard) and refresh in place.
  const [loading, setLoading] = useState(result === null);

  // Debounce the free-text search; other filters apply immediately.
  const [qDebounced, setQDebounced] = useState(query.q);
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(query.q), 350);
    return () => clearTimeout(t);
  }, [query.q]);

  // Page resets on a filter change are handled atomically by the store's
  // patchHistoryQuery — see the comment there. Doing it here as a follow-up
  // effect made every filter change fetch twice.

  useEffect(() => {
    void fetchHistoryFilters().then(setFilters).catch(() => { /* leave empty */ });
  }, [setFilters]);

  // Fetch on every effective-query change AND on mount — so returning shows the
  // cached rows instantly, then a background refresh replaces them.
  // Guard against out-of-order responses: only the latest request applies.
  const reqId = useRef(0);
  useEffect(() => {
    const id = ++reqId.current;
    setLoading(true);
    void fetchHistory({
      q: qDebounced,
      agentId: query.agentId,
      status: query.status,
      priority: query.priority,
      tag: query.tag,
      departmentId: query.departmentId,
      siteId: query.siteId,
      location: query.location,
      engagedOnly: query.engagedOnly,
      internal: query.internal,
      from: query.from,
      to: query.to,
      page: query.page,
      limit: query.limit,
    })
      .then((res) => {
        if (id !== reqId.current) return;
        setResult({ items: res.items, total: res.total });
      })
      .catch(() => {
        if (id !== reqId.current) return;
        setResult({ items: [], total: 0 });
      })
      .finally(() => { if (id === reqId.current) setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    qDebounced,
    query.agentId,
    query.status,
    query.priority,
    query.tag,
    query.departmentId,
    query.siteId,
    query.location,
    query.engagedOnly,
    query.internal,
    query.limit,
    query.from,
    query.to,
    query.page,
  ]);

  const pages = Math.max(1, Math.ceil(total / query.limit));
  const hasFilters = Boolean(
    query.q ||
      query.agentId ||
      query.status ||
      query.priority ||
      query.tag ||
      query.departmentId ||
      query.siteId ||
      query.location ||
      query.engagedOnly ||
      query.internal !== 'all' ||
      query.from ||
      query.to,
  );

  function reset() {
    resetQuery();
  }

  return (
    <div>
      <div className={styles.filters}>
        <label className={styles.field}>
          <span className={styles.label}>Status</span>
          <select className={styles.select} value={query.status} onChange={(e) => patch({ status: e.target.value as InboxStatusValue | '' })}>
            <option value="">Any status</option>
            {(Object.keys(STATUS_LABEL) as InboxStatusValue[]).map((v) => <option key={v} value={v}>{STATUS_LABEL[v]}</option>)}
          </select>
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Priority</span>
          <select className={styles.select} value={query.priority} onChange={(e) => patch({ priority: e.target.value as InboxPriorityValue | '' })}>
            <option value="">Any priority</option>
            {(Object.keys(PRIORITY_LABEL) as InboxPriorityValue[]).map((v) => <option key={v} value={v}>{PRIORITY_LABEL[v]}</option>)}
          </select>
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Tag</span>
          <select className={styles.select} value={query.tag} onChange={(e) => patch({ tag: e.target.value })}>
            <option value="">Any tag</option>
            {filters.tags.map((t) => <option key={t} value={t}>#{t}</option>)}
          </select>
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Department</span>
          <select className={styles.select} value={query.departmentId} onChange={(e) => patch({ departmentId: e.target.value })}>
            <option value="">Any department</option>
            <option value={NO_DEPARTMENT}>No department</option>
            {filters.departments
              .filter((d) => !query.siteId || d.siteId === query.siteId)
              .map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Site</span>
          <select className={styles.select} value={query.siteId} onChange={(e) => patch({ siteId: e.target.value })}>
            <option value="">All sites</option>
            {filters.sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Agent</span>
          <select className={styles.select} value={query.agentId} onChange={(e) => patch({ agentId: e.target.value })}>
            <option value="">All agents</option>
            <option value={UNASSIGNED}>Unassigned</option>
            {filters.agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Location</span>
          <select className={styles.select} value={query.location} onChange={(e) => patch({ location: e.target.value })}>
            <option value="">All locations</option>
            {filters.countries.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className={styles.field}>
          <span className={styles.label}>From</span>
          <input className={styles.input} type="date" value={query.from} max={query.to || undefined} onChange={(e) => patch({ from: e.target.value })} />
        </label>
        <label className={styles.field}>
          <span className={styles.label}>To</span>
          <input className={styles.input} type="date" value={query.to} min={query.from || undefined} onChange={(e) => patch({ to: e.target.value })} />
        </label>
        <label className={`${styles.field} ${styles.search}`}>
          <span className={styles.label}>Search (name, email, message text)</span>
          <input className={styles.input} type="search" value={query.q} placeholder="Search the conversation history…" onChange={(e) => patch({ q: e.target.value })} />
        </label>
        <label className={styles.field} style={{ flexDirection: 'row', alignItems: 'center', gap: 6, height: 34 }}>
          <input type="checkbox" checked={query.engagedOnly} onChange={(e) => patch({ engagedOnly: e.target.checked })} />
          <span className={styles.label} style={{ textTransform: 'none' }}>Engaged only</span>
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Internal</span>
          <select className={styles.select} value={query.internal} onChange={(e) => patch({ internal: e.target.value as 'all' | 'only' | 'hide' })}>
            <option value="all">All chats</option>
            <option value="hide">Hide internal</option>
            <option value="only">Internal only</option>
          </select>
        </label>
        {hasFilters && <button type="button" className={styles.ghost} onClick={reset}>Clear</button>}
      </div>

      {loading && items.length === 0 ? (
        <p className={styles.empty}>Loading…</p>
      ) : items.length === 0 ? (
        <p className={styles.empty}>No chats match these filters.</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr><th>Visitor</th><th>Status</th><th>Priority</th><th>Tags</th><th>Department</th><th>Site</th><th>Agent</th><th>Medium</th><th>Started</th><th>Ended</th><th>Location</th></tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.sessionId} className={styles.row} onClick={() => navigate(`/conversation/${it.sessionId}`)}>
                  <td>
                    {it.starred ? '★ ' : ''}{visitorDisplayName(it.visitorId, it.visitorName)}
                    {it.internal ? <span className={styles.badge} title="Connected from an internal (whitelisted) IP" style={{ marginLeft: 6 }}>Internal</span> : null}
                    {it.visitorEmail ? <span style={{ color: 'var(--inic-ink-muted)' }}> · {it.visitorEmail}</span> : null}
                  </td>
                  <td><span className={styles.badge}>{STATUS_LABEL[it.status] ?? it.status}</span></td>
                  <td>
                    <span className={styles.badge} data-priority={it.priority}>{PRIORITY_LABEL[it.priority] ?? it.priority}</span>
                  </td>
                  <td>
                    {it.tags.length
                      ? it.tags.map((t) => <span key={t} className={styles.tag}>#{t}</span>)
                      : <span style={{ color: 'var(--inic-ink-muted)' }}>—</span>}
                  </td>
                  <td>{it.departmentName ?? <span style={{ color: 'var(--inic-ink-muted)' }}>—</span>}</td>
                  <td>
                    {(() => {
                      const site = siteById.get(it.siteId);
                      const label = site?.name ?? '—';
                      return label === '—' ? (
                        <span style={{ color: 'var(--inic-ink-muted)' }}>—</span>
                      ) : (
                        <span className={styles.siteBadge} style={siteBadgeStyle(site)} title={label}>
                          {label}
                        </span>
                      );
                    })()}
                  </td>
                  <td>{it.agentName ?? <span className={styles.badge}>{it.ownedBy === 'ai' ? 'AI' : '—'}</span>}</td>
                  <td>{it.medium}</td>
                  <td>{formatDateTime(it.startedAt)}</td>
                  <td>{it.endedAt ? formatDateTime(it.endedAt) : <span className={styles.badge}>open</span>}</td>
                  <td>{it.location || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className={styles.pager}>
        <button type="button" className={styles.ghost} disabled={query.page <= 1 || loading} onClick={() => patch({ page: Math.max(1, query.page - 1) })}>← Prev</button>
        <span>Page {query.page} of {pages} · {total} chat{total === 1 ? '' : 's'}</span>
        <button type="button" className={styles.ghost} disabled={query.page >= pages || loading} onClick={() => patch({ page: Math.min(pages, query.page + 1) })}>Next →</button>
        <label className={styles.field} style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <span className={styles.label} style={{ textTransform: 'none' }}>Per page</span>
          <select className={styles.select} value={query.limit} onChange={(e) => patch({ limit: Number(e.target.value) })}>
            {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>
    </div>
  );
}
